import {Injectable} from '@angular/core';
import {BehaviorSubject, finalize, Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {
    ApiResourcesService,
    DEFAULT_CONTACT_BUTTON_TEXT,
    DEFAULT_CONTACT_BUTTON_URL,
    DEFAULT_REPORT_DISCLAIMER_TEXT,
    ITextResource,
    UpdateTextResourceRequest
} from '../api/api.resources.service';

@Injectable({
    providedIn: 'root'
})
export class TextResourcesService {
    private resourcesSubject = new BehaviorSubject<ITextResource | null>(null);
    private loaded = false;
    private loadingRequest?: Observable<ITextResource | null>;

    readonly resources$ = this.resourcesSubject.asObservable();

    constructor(private apiResourcesService: ApiResourcesService) {}

    loadOnce(): Observable<ITextResource | null> {
        if (this.loaded) {
            return of(this.resourcesSubject.value);
        }

        if (this.loadingRequest) {
            return this.loadingRequest;
        }

        this.loadingRequest = this.fetchResources().pipe(
            shareReplay({bufferSize: 1, refCount: false})
        );
        return this.loadingRequest;
    }

    refresh(): Observable<ITextResource | null> {
        return this.fetchResources();
    }

    updateResources(request: UpdateTextResourceRequest): Observable<ITextResource | null> {
        return this.apiResourcesService.updateResources(request).pipe(
            switchMap(() => this.refresh())
        );
    }

    get current(): ITextResource | null {
        return this.resourcesSubject.value;
    }

    get reportDisclaimerText(): string {
        return this.current?.reportDisclaimerText || DEFAULT_REPORT_DISCLAIMER_TEXT;
    }

    get contactButtonText(): string {
        return this.current?.contactButtonText || DEFAULT_CONTACT_BUTTON_TEXT;
    }

    get contactButtonUrl(): string {
        return this.current?.contactButtonUrl || DEFAULT_CONTACT_BUTTON_URL;
    }

    private fetchResources(): Observable<ITextResource | null> {
        return this.apiResourcesService.getResources().pipe(
            tap(resources => {
                this.resourcesSubject.next(resources);
                this.loaded = true;
            }),
            finalize(() => {
                this.loadingRequest = undefined;
            })
        );
    }
}
